import { useCallback, useEffect, useState } from 'react';
import { PERIODICOS_DRAWER_ACTION_VALUES } from '../components/PeriodicosDrawerManager';
import {
    EstadoExpuestoEnum,
    SelectionStatus,
    TAB_INDEX_TO_ESTADO
} from '../consts/periodicosTypes';
import useGlobalFilters from '../../../hooks/useGlobalFilters';
import useTableMenu from '../../../hooks/useTableMenu';
import {
    useGetExposedEmployeesQuery,
    useLazyGetExposedEmployeesQuery,
    useLazyGetEmployeesPendingPrestadorQuery,
    useLazyGetEmployeesPendingConvocatedQuery,
    useLazyGetEmployeesConvocatedQuery,
    useGenerarConvocatoriasMutation,
    useExportarPeriodicosMutation,
    useGenerarPdfResultadosConvocatoriaMutation,
    useGenerarPdfConvocatoriaMutation
} from '../../../services/periodicosApi';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

export const usePeriodicos = () => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [referenceSelectionStatus, setReferenceSelectionStatus] = useState<SelectionStatus>(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [open, setOpen] = useState<string>('');
    const [wasClearFilters, setWasClearFilters] = useState(false);
    const employeesFilters = useGlobalFilters();
    const employeesTableMenu = useTableMenu();
    const [showRiesgoError, setShowRiesgoError] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<any | null>(null);
    const [triggerGetAllExposedEmployees] = useLazyGetExposedEmployeesQuery();
    const [overrideData, setOverrideData] = useState<any[] | null>(null);
    const [fetchData, setFetchData] = useState<any>(null);
    const [setIsFetching] = useState(false);
    const [generarConvocatorias] = useGenerarConvocatoriasMutation();
    const ENDPOINTS_BY_TAB = [
        useLazyGetExposedEmployeesQuery,
        useLazyGetEmployeesPendingPrestadorQuery,
        useLazyGetEmployeesPendingConvocatedQuery,
        useLazyGetEmployeesConvocatedQuery
    ];
    const [exportarPeriodicos] = useExportarPeriodicosMutation();
    const [generarPdfConvocatorias] = useGenerarPdfConvocatoriaMutation();

    const [getData, result] = ENDPOINTS_BY_TAB[tabIndex]();
    const { t } = useTranslation('periodicos');

    const {
        clearFilters,
        extraFilters,
        filters,
        setFilters,
        setExtraFilters,
        handleSubmitSearch,
        handleChangeExtraFilters: handleGlobalChangeExtraFilters
    } = useGlobalFilters();

    const handleSearchAndClearSelection = () => {
        handleSubmitSearch();
        setSelectedIds([]);
    };

    useEffect(() => {
        setSelectedIds([]);
        setSelectedEmployee(null);
        setReferenceSelectionStatus(null);
        setShowRiesgoError(false);
    }, [tabIndex, filters]);

    const isFetching = result.isFetching ?? true;
    const exposedEmployeesData = result.data;
    const { anchorToEventElement, openMenu } = employeesTableMenu;

    const { handleChangeExtraFilters } = employeesFilters;

    const getUniqueIdByTabIndex = (item: any, tab: number): string => {
        switch (tab) {
            case 0:
                return `${item?.empleadoId}-${item?.riesgo?.id}`;
            case 1:
                return `${item?.id}-${item?.riesgo?.id}`;
            case 2:
                return `${item?.id}-${item?.riesgo?.id}`;
            case 3:
                return `${item?.id_empleado_examen}`;
            default:
                return '';
        }
    };

    const getColumnWidths = (tabIndex: number) => {
        const baseWidths = ['5%', '10%', '10%', '10%', '10%', '10%'];
        let extraColumns: string[] = [];
        if (tabIndex === 1) extraColumns.push('15%');
        if (tabIndex === 2 || tabIndex === 3) extraColumns.push('15%');

        if (tabIndex === 3) extraColumns.push('5%');
        const widths = [...baseWidths.slice(0, 3), ...extraColumns, ...baseWidths.slice(3)];

        widths.unshift('2%');
        widths.push('2%');

        return widths;
    };

    useEffect(() => {
        const fetch = async () => {
            try {
                const response = await getData(filters).unwrap();
                setFetchData(response);
            } catch (error) {
                setFetchData(null);
            } finally {
            }
        };
        fetch();
    }, [tabIndex, filters, exposedEmployeesData]);

    useEffect(() => {
        if (!exposedEmployeesData?.data?.data) return;

        if (selectedIds.length === 0) {
            setReferenceSelectionStatus(null);
            setShowRiesgoError(false);
            return;
        }
        const selectedItems = selectedIds
            .map((id) =>
                exposedEmployeesData.data.data.find((i: any) => {
                    if (tabIndex === 3) {
                        return `${i.id_empleado_examen}` === id;
                    }
                    return `${i.empleadoId || i.id}-${i.riesgo?.id}` === id;
                })
            )
            .filter(Boolean);

        if (selectedItems.length > 0) {
            const first = selectedItems[0];

            setReferenceSelectionStatus({
                hasExam: !!first?.examen && first.examen !== 'NO',
                hasPrestador: !!first?.prestador && first.prestador !== 'NO',
                riesgoCodes: first?.riesgo?.codigo ? [first.riesgo.codigo] : []
            });

            if (tabIndex === 0) {
                const allHaveSameRiesgo = selectedItems.every((item) => {
                    const currentCodigo = item?.riesgo?.codigo;
                    return currentCodigo === first?.riesgo?.codigo;
                });

                setShowRiesgoError(!allHaveSameRiesgo);
            } else {
                setShowRiesgoError(false);
            }
        }
    }, [selectedIds, exposedEmployeesData]);

    const handleSelectEmployee = (item: any) => {
        const uniqueId = getUniqueIdByTabIndex(item, tabIndex);
        const isSelected = selectedIds.includes(uniqueId);
        const newSelected = isSelected
            ? selectedIds.filter((id) => id !== uniqueId)
            : [...selectedIds, uniqueId];
        setSelectedIds(newSelected);
    };

    const handleClickAction = (event: any, item: any, index: any) => {
        const empleadoId = item?.empleadoId || item?.id;
        const riesgoId = item?.riesgo?.id;

        const uniqueId = getUniqueIdByTabIndex(item, tabIndex);

        setSelectedEmployee(item);
        setSelectedIds([uniqueId]);
        anchorToEventElement(event);
        openMenu();
    };
    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setSelectedIds([]);
        setTabIndex(newValue);

        const idEstado = TAB_INDEX_TO_ESTADO[newValue];
        handleChangeExtraFilters({
            target: { name: 'idEstado', value: idEstado }
        });
    };

    const handleAsignarPrestador = () => {
        if (selectedIds.length > 0) {
            setOpen(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_PRESTADOR);
        }
    };

    const handleAsignarExamen = () => {
        if (selectedIds.length > 0) {
            setOpen(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_EXAM);
        }
    };

    const handleGenerarConvocatorias = async () => {
        if (selectedIds.length === 0) return;

        const idsEmpleadosExamenes = selectedIds.map((id) => Number(id.split('-')[0]));

        try {
            const response = await generarConvocatorias({ idsEmpleadosExamenes }).unwrap();
            const idConvocatoria = Number(response?.data);

            enqueueSnackbar('Convocatoria generada correctamente.', {
                variant: 'success'
            });

            setSelectedIds([]);
            reloadGrid(true);
        } catch (error: any) {
            enqueueSnackbar(error?.data?.message || 'Ocurrió un error al descargar el PDF', {
                variant: 'error'
            });
        }
    };

    const descargarPdfConvocatoria = async (idConvocatoria: number) => {
        const pdfResponse = await generarPdfConvocatorias(idConvocatoria).unwrap();

        const base64Data = pdfResponse?.data;
        const byteCharacters = atob(base64Data);
        const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Detalle_convocatoria_${idConvocatoria}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const handleAsignarTurno = () => {
        if (selectedIds.length > 0) {
            setOpen(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_SHIFT);
        }
    };

    const handleExportarExcel = async () => {
        try {
            const response = await exportarPeriodicos(filters).unwrap();

            const byteArray = new Uint8Array(response.data);
            const blob = new Blob([byteArray], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Reporte-Convocados.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();

            enqueueSnackbar(t('alerts.exportExcel.generateSuccess'), { variant: 'success' });
        } catch (error: any) {
            error?.data?.message || t('alerts.exportExcel.generateError');
        }
    };

    const handleClearFilters = () => {
        setWasClearFilters(true);
        clearFilters();
    };

    useEffect(() => {
        if (wasClearFilters) {
            handleTabChange({} as React.SyntheticEvent, 0);
            setWasClearFilters(false);
        }
    });

    const reloadGrid = async (goToNextTab = false) => {
        try {
            const response = await getData(filters).unwrap();
            setFetchData(response);
            setSelectedIds([]);
            await handleSubmitSearch();
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        setFetchData(null);
    }, [tabIndex]);

    const handleSelectAllEmployed = async (visibleItems: any[], isChecked: boolean) => {
        const total = exposedEmployeesData?.data?.count || 0;

        if (!isChecked) {
            setSelectedIds([]);
            return;
        }

        if (filters.take >= total) {
            const allVisibleIds = visibleItems.map((item: any) =>
                getUniqueIdByTabIndex(item, tabIndex)
            );
            setSelectedIds(allVisibleIds);
            return;
        }

        const updatedFilters = {
            ...filters,
            take: total,
            skip: 0
        };

        setFilters(updatedFilters);

        try {
            const { data } = await getData(updatedFilters).unwrap();
            setFetchData({ data: data.data, count: data.count });

            const allIds = data.data.map((item: any) => getUniqueIdByTabIndex(item, tabIndex));
            setSelectedIds(allIds);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        const total = fetchData?.data?.count || 0;
        const data = fetchData?.data?.data || [];

        if (filters.take === total && data.length === total) {
            const allIds = data.map((item: any) => getUniqueIdByTabIndex(item, tabIndex));
            setSelectedIds(allIds);
        }
    }, [exposedEmployeesData, filters.take]);

    return {
        employeesTableMenu,
        selectedEmployee,
        handleClickAction,
        employeesFilters,
        selectedIds,
        setSelectedIds,
        tabIndex,
        setTabIndex,
        open,
        setOpen,
        wasClearFilters,
        handleSelectEmployee,
        handleTabChange,
        handleAsignarPrestador,
        handleAsignarExamen,
        handleGenerarConvocatorias,
        handleAsignarTurno,
        handleExportarExcel,
        handleClearFilters,
        extraFilters,
        filters,
        setFilters,
        reloadGrid,
        handleSubmitSearch,
        handleChangeExtraFilters: handleGlobalChangeExtraFilters,
        showRiesgoError,
        handleSelectAllEmployed,
        fetchData,
        getUniqueIdByTabIndex,
        isFetching,
        getColumnWidths,
        handleSearchAndClearSelection,
        getData,
        descargarPdfConvocatoria
    };
};
